import { FC, ReactNode, useCallback, useMemo } from "react";
import Badge from "../../../components/shared/Badge/Badge";
import Loading from "../../../components/shared/Loading";
import StyledCheckbox from "../../../components/shared/StyledCheckbox/StyledCheckbox";
import TooltipContainer from "../../../components/shared/TooltipContainer/TooltipContainer";
import styles from "./MassMessagingModal.module.scss";
import { AreaSelectionType, Contact, Purchase, RecipientListType, Supplier } from "./MassMessagingModal";
import { Trans, useTranslation } from "react-i18next";
import { ErrorMessageIcon } from "../../../components/shared/ErrorMessageIcon/ErrorMessageIcon";
import { ReactComponent as LightArrow } from '../../../assets/svg/lightArrow.svg';
import { ReactComponent as InfoIcon } from '../../../assets/svg/InfoIconLight.svg'
import { format } from "date-fns";

type Props = {
  recipientSelection: {[x: string]: AreaSelectionType},
  setRecipientSelection: React.Dispatch<React.SetStateAction<{[x: string]: AreaSelectionType}>>,
  purchases: Purchase[],
  aboveLimitErrorMessage?: ReactNode,
  shownRecipients: string[],
  setShownRecipients: React.Dispatch<React.SetStateAction<string[]>>,
  loading: boolean,
  recipientList: RecipientListType,
  massMessagingOrgInfo?: {messagesSentWeekly: {[x: string]: {amount: number, limit: number}}, singleMessageLimit: number, weeklyMessagesLimit: number},
}

const MassMessagingRecipientSelector : FC<Props> = ({ 
  recipientSelection, setRecipientSelection, 
  shownRecipients, setShownRecipients, 
  purchases, 
  aboveLimitErrorMessage, 
  massMessagingOrgInfo,
  loading,
  recipientList,
}) => {
  const SINGLE_LIMIT = massMessagingOrgInfo?.singleMessageLimit || 200;
  const WEEKLY_LIMIT = massMessagingOrgInfo?.weeklyMessagesLimit || 600;
  const weeklyInfo = massMessagingOrgInfo?.messagesSentWeekly;
  const infoThisWeek = weeklyInfo?.[format(new Date(), 'YYYY-ww', {useAdditionalWeekYearTokens: true,})] || {amount: 0, limit: WEEKLY_LIMIT};
  
  const [t] = useTranslation('projects', { keyPrefix: 'massMessaging' });
  
  const getSupplierBadge = useCallback((supplier: Supplier) => {
    let badgeProps: {backgroundColor?: string, color?: string} = {};
    let badgeText = '';
    if (supplier.won) {
      badgeText = t('badge.winning', 'Vinnande leverantörer');
      badgeProps = { backgroundColor: 'var(--yellow-100)', color: 'var(--yellow-800)' };
    } else if (supplier.intent === 'DECLINED') {
      badgeText = t('declineIntent', 'Avser inte lämna anbud');
      badgeProps = { backgroundColor: 'var(--red-100)', color: 'var(--red-800)' };
    } else if (supplier.intent === 'ACCEPTED') {
      badgeText = t('acceptIntent', 'Avser lämna anbud');
      badgeProps = { backgroundColor: 'var(--green-100)', color: 'var(--green-800)' };
    }
    if (badgeText) {
      return <Badge className={styles.supplierBadge} {...badgeProps}>{badgeText}</Badge>;
    }
  }, []);

  const checkSupplier = useCallback((checked: boolean, purchase: Purchase, supplier: Supplier) => {
    setRecipientSelection(prevMassMessagingRecipientSelection => {
      const newMassMessagingRecipientSelection = {...prevMassMessagingRecipientSelection};
      let newAreaSelection: AreaSelectionType = {...(newMassMessagingRecipientSelection[purchase.areaId] as Record<string, boolean>) , [supplier.supplierId]: checked};
      if (checked) {
        if (Object.entries(newAreaSelection).filter(([_, value]) => value === true).length === purchase.suppliers.length) {
          newAreaSelection = true;
        }
      } else {
        if (newMassMessagingRecipientSelection[purchase.areaId] === true) {
          newAreaSelection = { ...purchase.suppliers.reduce((acc: Record<string, boolean>, current) => {
            acc[current.supplierId] = true;
            return acc;
          }, {}), [supplier.supplierId]: false };

          if (Object.values(newAreaSelection).filter(value => value).length === 0) {
            newAreaSelection = false;
          }
        } else if (newMassMessagingRecipientSelection[purchase.areaId] !== true && Object.entries(newAreaSelection).filter(([_, value]) => value).length === 0) {
          newAreaSelection = false;
        }
      }

      return { ...newMassMessagingRecipientSelection, [purchase.areaId]: newAreaSelection }
    });
  }, []);

  const checkPurchase = useCallback((checked: boolean, purchase: Purchase) => {
    setRecipientSelection(prevState => ({ ...prevState, [purchase.areaId]: checked }));
  }, []);

  const checkContact = useCallback((checked: boolean, purchase: Purchase, supplier: Supplier, contact: Contact) => {
    setRecipientSelection(prevMassMessagingRecipientSelection => {
      const newMassMessagingRecipientSelection = {...prevMassMessagingRecipientSelection};

      const currentSupplierSelection = (newMassMessagingRecipientSelection[purchase.areaId] as Record<string, boolean>)?.[supplier.supplierId] as (Record<string, boolean> | boolean);
      let newSupplierSelection : Record<string, boolean> | boolean = {...currentSupplierSelection as Record<string, boolean>, [contact.id]: checked};
      if (checked) {
        if (Object.entries(newSupplierSelection).filter(([_, value]) => value).length === supplier.contacts.length) {
          newSupplierSelection = true;
        }
      } else {
        if (currentSupplierSelection === true || newMassMessagingRecipientSelection[purchase.areaId] === true) {
          if (supplier.contacts.length > 1) {
            newSupplierSelection = { ...supplier.contacts.reduce((acc: Record<string, boolean>, current) => {acc[current.id] = true; return acc;}, {}), [contact.id]: false };
          } else {
            newSupplierSelection = false;
          }
        } else if (Object.entries(newSupplierSelection).filter(([_, value]) => value).length === 0) {
          newSupplierSelection = false;
        }
      }

      let newAreaSelection: AreaSelectionType = {...newMassMessagingRecipientSelection[purchase.areaId] as Record<string, boolean>, [supplier.supplierId]: newSupplierSelection};
      if (newMassMessagingRecipientSelection[purchase.areaId] === true && purchase.suppliers.length > 1) {
        newAreaSelection = { ...purchase.suppliers.reduce((acc: Record<string, boolean>, current) => {acc[current.supplierId] = true; return acc;}, {}), [supplier.supplierId]: newSupplierSelection };
      }

      if (newSupplierSelection === false && Object.entries(newAreaSelection).filter(([_, value]) => value).length === 0) {
        newAreaSelection = false;
      } else if (newSupplierSelection === true && Object.entries(newAreaSelection).every(([_, value]) => value) && Object.entries(newAreaSelection).length === purchase.suppliers.length) {
        newAreaSelection = true;
      }

      return { ...newMassMessagingRecipientSelection, [purchase.areaId]: newAreaSelection }
    })
  }, []);



    const recipientsByFilter = useMemo(() => {
    const filteredRecipients: {[key: string]: {areaId: string, supplierId: string}[]} = {
      'winning': [],
      'not-winning': [],
      'intent-accepted': [],
      'intent-declined': [],
      'has-bid': [],
    }

    for (const purchase of purchases) {
      for (const supplier of purchase.suppliers) {
        if (supplier.won) {
          filteredRecipients['winning'].push({areaId: purchase.areaId, supplierId: supplier.supplierId});
        } else {
          filteredRecipients['not-winning'].push({areaId: purchase.areaId, supplierId: supplier.supplierId});
        }
        if (supplier.intent === 'ACCEPTED') {
          filteredRecipients['intent-accepted'].push({areaId: purchase.areaId, supplierId: supplier.supplierId});
        } else if (supplier.intent === 'DECLINED') {
          filteredRecipients['intent-declined'].push({areaId: purchase.areaId, supplierId: supplier.supplierId});
        }
        if (supplier.hasBid) {
          filteredRecipients['has-bid'].push({areaId: purchase.areaId, supplierId: supplier.supplierId});
        }
      }
    }
    return filteredRecipients;
  }, [purchases]);

  return (<>
    <div className={styles.quickSelectContainer}>
      <span>{t('quickSelect.heading', 'Snabbval')}</span>
      <div className={styles.filterContainer}>
        <label className={`${recipientsByFilter['winning'].length === 0 ? styles.disabledQuickSelect : ''}`}>
          <StyledCheckbox
            onChange={async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
              for (const purchase of purchases) {
                const suppliersToCheck = purchase.suppliers.filter(supplier => supplier.won);
                for (const supplier of suppliersToCheck) {
                  checkSupplier(checked, purchase, supplier);
                }
              }
            }}
            checked={recipientsByFilter['winning'].some(x => recipientList.find(y => y.areaId === x.areaId && y.supplierId === x.supplierId))}
            dash={recipientsByFilter['winning'].length !== recipientList.length && !recipientsByFilter['winning'].every(x => recipientList.find(y => y.areaId === x.areaId && y.supplierId === x.supplierId))}
          />
          <span>{t('quickSelect.label.winningSuppliers', 'Vinnande leverantörer')}</span>
        </label>
        <label className={`${recipientsByFilter['not-winning'].length === 0 ? styles.disabledQuickSelect : ''}`}>
          <StyledCheckbox
            onChange={async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
              for (const purchase of purchases) {
                const suppliersToCheck = purchase.suppliers.filter(supplier => !supplier.won);
                for (const supplier of suppliersToCheck) {
                  checkSupplier(checked, purchase, supplier);
                }
              }
            }}
            checked={recipientsByFilter['not-winning'].some(x => recipientList.find(y => y.areaId === x.areaId && y.supplierId === x.supplierId))}
            dash={recipientsByFilter['not-winning'].length !== recipientList.length && !recipientsByFilter['not-winning'].every(x => recipientList.find(y => y.areaId === x.areaId && y.supplierId === x.supplierId))}
          />
          <span>{t('quickSelect.label.notWinningSuppliers', 'Ej vinnande leverantörer')}</span>
        </label>
        <label className={`${recipientsByFilter['intent-accepted'].length === 0 ? styles.disabledQuickSelect : ''}`}>
          <StyledCheckbox
            onChange={async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
              for (const purchase of purchases) {
                const suppliersToCheck = purchase.suppliers.filter(supplier => supplier.intent === 'ACCEPTED');
                for (const supplier of suppliersToCheck) {
                  checkSupplier(checked, purchase, supplier);
                }
              }
            }}
            checked={recipientsByFilter['intent-accepted'].some(x => recipientList.find(y => y.areaId === x.areaId && y.supplierId === x.supplierId))}
            dash={recipientsByFilter['intent-accepted'].length !== recipientList.length && !recipientsByFilter['intent-accepted'].every(x => recipientList.find(y => y.areaId === x.areaId && y.supplierId === x.supplierId))}
          />
          <span>{t('quickSelect.label.acceptIntent', 'Avser lämna anbud')}</span>
        </label>
        <label className={`${recipientsByFilter['intent-declined'].length === 0 ? styles.disabledQuickSelect : ''}`}>
          <StyledCheckbox
            onChange={async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
              for (const purchase of purchases) {
                const suppliersToCheck = purchase.suppliers.filter(supplier => supplier.intent === 'DECLINED');
                for (const supplier of suppliersToCheck) {
                  checkSupplier(checked, purchase, supplier);
                }
              }
            }}
            checked={recipientsByFilter['intent-declined'].some(x => recipientList.find(y => y.areaId === x.areaId && y.supplierId === x.supplierId))}
            dash={recipientsByFilter['intent-declined'].length !== recipientList.length && !recipientsByFilter['intent-declined'].every(x => recipientList.find(y => y.areaId === x.areaId && y.supplierId === x.supplierId))}
          />
          <span>{t('quickSelect.label.declineIntent', 'Avser inte lämna anbud')}</span>
        </label>
        <label className={`${recipientsByFilter['has-bid'].length === 0 ? styles.disabledQuickSelect : ''}`}>
          <StyledCheckbox
            onChange={async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
              for (const purchase of purchases) {
                const suppliersToCheck = purchase.suppliers.filter(supplier => supplier.hasBid);
                for (const supplier of suppliersToCheck) {
                  checkSupplier(checked, purchase, supplier);
                }
              }
            }}
            checked={recipientsByFilter['has-bid'].some(x => recipientList.find(y => y.areaId === x.areaId && y.supplierId === x.supplierId))}
            dash={recipientsByFilter['has-bid'].length !== recipientList.length && !recipientsByFilter['has-bid'].every(x => recipientList.find(y => y.areaId === x.areaId && y.supplierId === x.supplierId))}
          />
          <span>{t('quickSelect.label.hasBid', 'Lämnat anbud')}</span>
        </label>
      </div>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', height: '20px' }}>
        <TooltipContainer
          renderReferenceComponent={
            (className, ref) => <span ref={ref} className={className} style={{ width: 'fit-content' }}>{t('recipients.heading', 'Välj mottagare ({{count}})', { count: recipientList.length })}</span>
          }
        >
          {recipientList.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', maxWidth: '400px' }}>
              {recipientList.map(recipient => (
                <Badge
                  key={`${recipient.areaId}${recipient.supplierId}${recipient.contact.id}`}
                  backgroundColor='var(--gray-200)' color='var(--gray-800)' style={{ width: 'fit-content' }}
                >
                  {recipient.contact.name}
                </Badge>
              ))}
            </div>
          )}
        </TooltipContainer>

        {!aboveLimitErrorMessage && <TooltipContainer renderReferenceComponent={(className, ref) => <InfoIcon ref={ref} className={className} style={{ width: 'fit-content' }} />}>
          <div style={{ fontSize: '12px', maxWidth: '400px' }}>
            <Trans t={t} i18nKey="limitInfo" values={{ singleLimit: SINGLE_LIMIT, weeklyLimit: WEEKLY_LIMIT, count: WEEKLY_LIMIT - infoThisWeek.amount }} components={{b: <b />}} />
          </div>
        </TooltipContainer>}

        {aboveLimitErrorMessage && <ErrorMessageIcon errorMessage={aboveLimitErrorMessage} />}
      </div>

      {purchases?.length ? (
        <div className={styles.treeView}>
          <label style={{ width: 'fit-content' }}><StyledCheckbox checked={Object.entries(recipientSelection).filter(([_, value]) => value).length > 0} dash={Object.entries(recipientSelection).filter(([_, value]) => value === true).length !== purchases.length}
            onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
              setRecipientSelection(
                checked ? purchases.reduce((acc: Record<string, boolean>, current) => {
                  acc[current.areaId] = true;
                  return acc;
                }, {}) :
                {}
              );
            }}
          />
            <span>{t('label.allPurchases', 'Alla inköp')}</span>
          </label>
          {purchases.filter(purchase => !!purchase).map(purchase => {
            return (
              <div key={purchase.areaId} className={`${styles.smallIndentation} ${shownRecipients.includes(purchase.areaId) ? styles.show : ''}`}>
                <div className={styles.purchaseName}>
                  <LightArrow
                    onClick={() => {
                      setShownRecipients(shownRecipients.includes(purchase.areaId) ? shownRecipients.filter(y => y !== purchase.areaId || !y.startsWith(purchase.areaId)) : [...shownRecipients, purchase.areaId]);
                    }}
                    className={`${styles.indentationArrow} ${styles.areaArrow}`}
                  />
                  <label>
                    <StyledCheckbox
                      checked={!!recipientSelection[purchase.areaId]}
                      dash={recipientSelection[purchase.areaId] !== true}
                      onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => checkPurchase(checked, purchase)}
                    />
                    <span>{purchase.areaName}</span>
                  </label>
                </div>
                {purchase.suppliers.map(supplier => {
                  return (
                    <div key={`${purchase.areaId}${supplier.supplierId}`} className={`${styles.indent} ${shownRecipients.includes(`${purchase.areaId}${supplier.supplierId}`) ? styles.show : ''}`}>
                      <div style={{ display: 'flex', alignItems: 'center',}}>
                        <LightArrow className={`${styles.indentationArrow} ${styles.supplierArrow}`} onClick={() => setShownRecipients(shownRecipients.includes(`${purchase.areaId}${supplier.supplierId}`) ? shownRecipients.filter(y => y !== `${purchase.areaId}${supplier.supplierId}`) : [...shownRecipients, `${purchase.areaId}${supplier.supplierId}`])} />
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <label>
                            <StyledCheckbox checked={!!(recipientSelection[purchase.areaId] as Record<string, boolean>)?.[supplier.supplierId] || recipientSelection[purchase.areaId] === true} dash={(recipientSelection[purchase.areaId] as Record<string, boolean>)?.[supplier.supplierId] !== true && recipientSelection[purchase.areaId] !== true} onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => checkSupplier(checked, purchase, supplier)}/>
                            <span>{supplier.supplierName}</span>
                          </label>
                          {getSupplierBadge(supplier)}
                        </div>
                      </div>
                      {supplier.contacts.map(contact => {
                        return (
                          <div key={`${purchase.areaId}${contact.id}`} className={styles.bigIndentation}>
                            <label>
                              <StyledCheckbox
                                checked={!!(recipientSelection[purchase.areaId] as Record<string, Record<string, boolean>>)?.[supplier.supplierId]?.[contact.id] || (recipientSelection[purchase.areaId] as Record<string, boolean>)?.[supplier.supplierId] === true || recipientSelection[purchase.areaId] === true}
                                onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => checkContact(checked, purchase, supplier, contact)}
                              />
                              <span>{`${contact.name}${contact.title ? `, ${contact.title}` : ''} <${contact.email}>`}</span>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      ) : (
        <span style={{ alignSelf: 'center', marginBottom: '16px' }}>
          {loading ? <Loading type='inline' /> : t('errorMessage.noRecipients', 'Du har inga mottagare')}
        </span>
      )}
    </div>
  </>);  
}

export default MassMessagingRecipientSelector;